// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xGoogleAnalyticsState } from "@guestsupply/App/Store/Reducers/xGoogleAnalytics";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { parseProductCategories } from "@guestsupply/XDL/Utilities";
import { replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    chain as PlaceOrderChain,
    SendTracking,
} from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";

// <Xng-Change>
interface OrderItem {
    ItemNumber: string;
    StockCode: string;
    Quantity: number;
    Price: number;
    LineTotalRaw: number;
    LineTotal: number;
    LineDiscountRaw: number;
    LineDiscount: number;
    ItemDiscountRaw: number;
    ItemDiscount: number;
    LineTotalWithDiscountRaw: number;
    LineTotalWithDiscount: number;
    IsPromotionalItem: boolean;
}

interface Promotion {
    DiscountId: string;
    ContractDescription: string;
    ItemTotalAllItems: number;
    DiscountAmount: number;
    DiscountPercent: number;
    CurrencyCode: string;
    OrderItems: OrderItem[];
}
// </Xng-Change>

export const _GoogleAnalyticsStatePlaceOrder: typeof SendTracking = props => {
    const state = props.getState() as xGoogleAnalyticsState;

    if (state.xGoogleAnalytics.includePurchaseEvent) {
        const dataLayer = getDataLayer();
        const session = getSession(state);
        const locationId = session?.pickUpWarehouse?.id;
        const currentBillTo = getCurrentBillToState(state).value;

        if (!dataLayer) {
            return;
        }
        const { apiResult, cartToUpdate } = props;

        dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.

        let ootbPromoObject: Promotion | undefined;
        const rawPromo = apiResult?.cart?.properties?.ootbPromoObject;
        if (typeof rawPromo === "string") {
            ootbPromoObject = JSON.parse(rawPromo) as Promotion;
        } else if (rawPromo && typeof rawPromo === "object") {
            ootbPromoObject = rawPromo as Promotion;
        }

        const promoItemsByErp = ootbPromoObject?.OrderItems?.reduce((acc, item) => {
            acc[item.ItemNumber] = item;
            return acc;
        }, {} as Record<string, OrderItem>);

        const googleAnalyticsData = {
            event: GOOGLE_EVENTS.PURCHASE,
            ecommerce: {
                transaction_id: apiResult?.cart?.orderNumber,
                value: apiResult?.cart?.orderSubTotal,
                tax: apiResult?.cart?.totalTax,
                shipping: apiResult?.cart?.shippingAndHandling,
                currency: state.context.session.currency?.currencyCode,
                coupon_value: apiResult?.cart?.properties?.discountAmount,
                promotion: {
                    discountId: ootbPromoObject?.DiscountId,
                    contractDescription: ootbPromoObject?.ContractDescription,
                    itemTotalAllItems: ootbPromoObject?.ItemTotalAllItems,
                    discountAmount: ootbPromoObject?.DiscountAmount,
                    discountPercent: ootbPromoObject?.DiscountPercent,
                    currencyCode: ootbPromoObject?.CurrencyCode,
                },
                items: cartToUpdate?.cartLines?.map(cartLine => {
                    const promoItem = promoItemsByErp?.[cartLine.erpNumber];
                    const item: any = {
                        item_id: cartLine.erpNumber,
                        item_name: cartLine.shortDescription,
                        affiliation: apiResult?.billTo?.companyName,
                        index: cartLine?.line,
                        item_brand: cartLine.brand?.name,
                        location_id: locationId,
                        price: cartLine.pricing?.unitNetPrice,
                        quantity: cartLine.qtyOrdered,
                        ...parseProductCategories(cartLine.properties?.categories),
                    };

                    if (promoItem) {
                        item.stock_code = promoItem.StockCode;
                        item.line_total = promoItem.LineTotal;
                        item.line_discount = promoItem.LineDiscount;
                        item.item_discount = promoItem.ItemDiscount;
                        item.line_total_with_discount = promoItem.LineTotalWithDiscount;
                        item.is_promotional_item = promoItem.IsPromotionalItem;
                    }
                    return item;
                }),
                user: state.context.session.email || state.context.session.userName,
                brand_code: apiResult?.billTo?.properties?.brand,
                exclusive: apiResult?.billTo?.properties?.exclusive,
                group_descr: apiResult?.billTo?.properties?.groupDescription,
                sales_rep: cartToUpdate?.salespersonName,
                customer_number: currentBillTo?.customerNumber,
            },
        };

        dataLayer.push(googleAnalyticsData);
    }
};

replaceInChain(PlaceOrderChain, SendTracking, _GoogleAnalyticsStatePlaceOrder);
